import { genres, regions, sortOptions, years } from "@/mocks/movies";

export type CategoryFilters = {
  type: string;
  region: string;
  year: string;
  sort: string;
};

type FilterPanelProps = {
  filters: CategoryFilters;
  onChange: (key: keyof CategoryFilters, value: string) => void;
  onReset: () => void;
  resultCount: number;
};

function FilterRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 border-b border-background-200/80 py-4 last:border-b-0 sm:flex-row sm:items-start sm:gap-5">
      <span className="shrink-0 pt-1.5 font-label text-xs uppercase tracking-[0.16em] text-foreground-500 sm:w-16">
        {label}
      </span>
      <div className="flex flex-wrap gap-2">{children}</div>
    </div>
  );
}

function Chip({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`cursor-pointer whitespace-nowrap rounded-full px-3.5 py-1.5 text-xs transition-colors ${
        active
          ? "bg-primary-500 font-medium text-background-50"
          : "border border-background-300 bg-background-50 text-foreground-700 hover:border-primary-300 hover:text-primary-700"
      }`}
    >
      {children}
    </button>
  );
}

export default function FilterPanel({ filters, onChange, onReset, resultCount }: FilterPanelProps) {
  const typeOptions = ["全部", ...genres.map((genre) => genre.name)];

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
      <div className="flex items-center justify-between border-b border-background-200 pb-3">
        <div className="flex items-center gap-2">
          <i className="ri-equalizer-line text-base w-4 h-4 flex items-center justify-center text-primary-600" />
          <h2 className="text-sm font-medium text-foreground-950">筛选与排序</h2>
          <span className="rounded-full bg-background-200 px-2 py-0.5 font-label text-[11px] text-foreground-600">
            {resultCount} 部
          </span>
        </div>
        <button
          type="button"
          onClick={onReset}
          className="flex cursor-pointer items-center gap-1 whitespace-nowrap text-xs text-foreground-500 transition-colors hover:text-primary-700"
        >
          <i className="ri-refresh-line text-sm w-4 h-4 flex items-center justify-center" />
          重置
        </button>
      </div>

      <FilterRow label="类型">
        {typeOptions.map((option) => (
          <Chip
            key={option}
            active={filters.type === option}
            onClick={() => onChange("type", option)}
          >
            {option}
          </Chip>
        ))}
      </FilterRow>

      <FilterRow label="地区">
        {regions.map((option) => (
          <Chip
            key={option}
            active={filters.region === option}
            onClick={() => onChange("region", option)}
          >
            {option}
          </Chip>
        ))}
      </FilterRow>

      <FilterRow label="年份">
        {years.map((option) => (
          <Chip
            key={option}
            active={filters.year === option}
            onClick={() => onChange("year", option)}
          >
            {option}
          </Chip>
        ))}
      </FilterRow>

      <FilterRow label="排序">
        {sortOptions.map((option) => (
          <Chip
            key={option.key}
            active={filters.sort === option.key}
            onClick={() => onChange("sort", option.key)}
          >
            {option.label}
          </Chip>
        ))}
      </FilterRow>
    </div>
  );
}